/**
 * Pure result mapper: Slice 5 workloads `ValidationReport` → the existing
 * local `CompanyShellValidateResult` shape consumed by
 * `mapValidationErrorsToCells.ts`, `BulkCreateCompanyReviewScreen`, and
 * `CompanyRowsTable`.
 *
 * LADR-039 (#440) Step 15 — see `.plans/ladr-039-contract-mapping.md`'s
 * "company shell (bulk-create-company)" vertical table for the authoritative
 * field mapping this module implements. No React/hooks here; safe to unit
 * test directly and to reuse from the workloads-backed manager without
 * pulling in TanStack Query.
 *
 * **Residual/behavioural note — the `"*"` column sentinel means the
 * OPPOSITE thing in Track 2 vs. this repo's existing Track 1 convention:**
 * `mapValidationErrorsToCells.ts`'s `WHOLE_INVOCATION_COLUMN_SENTINEL`
 * comment defines `column: "*"` as "this error isn't about any specific
 * row" (always routed to `batchErrors`), and expects a row-level-but-no-
 * specific-column error (e.g. "Company Code already exists") to omit
 * `column` entirely (routed to `rowErrors`). The generated
 * `RowValidationError.column` docstring says the opposite: `"'*' when the
 * error is row-level (e.g. saga failure, cross-field rule)"` — i.e. Track 2
 * uses `"*"` for exactly the case Track 1 represents with an *absent*
 * `column`. `toCompanyShellValidationError` below maps Track 2's `"*"` to
 * `column: undefined` so those errors land in `mapValidationErrorsToCells`'s
 * `rowErrors`/`errorsByRow` buckets (tied to the row, as Track 2 intends)
 * instead of silently detaching them into `batchErrors`. One consequence:
 * `RowValidationError`'s own docstring gives no "not tied to any row"
 * convention (`row_index` is documented as always a real `@minimum 0` index
 * into the rows array) — Track 2 has **no discovered carrier** for Track 1's
 * true whole-invocation case (the contract note's example was "a
 * scope-assertion failure"). `batchErrors` will therefore only ever be
 * populated by `mapValidationErrorsToCells`'s own out-of-range `row_index`
 * bounds-check, never by a dedicated sentinel — flagged here rather than
 * assumed away, per `.plans/ladr-039-contract-mapping.md`'s guidance not to
 * silently drop this kind of gap.
 *
 * **Residual — no `normalisations` carrier (contract note, "Summary of
 * residual mismatches" #3):** neither `ValidationReport`, `ValidationView`,
 * nor `EntityRecordView` in the regenerated client exposes anything
 * matching Track 1's `CompanyShellNormalisation[]` (@AC-UPLOAD-15 display
 * normalisation, e.g. a reformatted IRD number). This mapper always returns
 * `normalisations: undefined` — `CompanyRowsTable`'s "Normalised: …" hint
 * line simply won't render on the workloads path until the Api team either
 * confirms this ships silently server-side or adds a read-back surface. Do
 * not remove this comment/assumption without that confirmation.
 *
 * **Residual — `errors` vs `warnings` merge:** unlike work patterns/pay
 * components (which keep warnings separate and let the user acknowledge them
 * before committing), `CompanyShellValidateResult` has always had a single
 * flat `errors` array, and `BulkCreateCompanyReviewScreen.canCommit` blocks
 * on `errors.length > 0` with no severity branching and no
 * warning-acknowledgment UX. To preserve that pre-existing behaviour
 * unchanged, this mapper merges `ValidationReport.errors` *and*
 * `.warnings` into one list — a Christmas dry-run advisory will still block
 * Commit on the workloads path, exactly as any other entry in Track 1's
 * `errors[]` always did. `severity` is read from each `RowValidationError`
 * when present, defaulting to `"error"`/`"warning"` respectively per which
 * report array the item came from (the field is optional on the generated
 * type; `RowSeverity`'s two values, `"error"`/`"warning"`, are confirmed a
 * superset of what `mapValidationErrorsToCells` and the review screen key
 * off today — plain message display, no branching on the value).
 */
import {
  RowSeverity,
  type RowValidationError,
  type ValidationReport,
} from "../../generated/core-api";
import type {
  CompanyShellValidationError,
  CompanyShellValidateResult,
} from "../types";

function toCompanyShellValidationError(
  error: RowValidationError,
  defaultSeverity: string
): CompanyShellValidationError {
  return {
    row_index: error.row_index,
    column: error.column === "*" ? undefined : error.column,
    message: error.message,
    severity: error.severity ?? defaultSeverity,
  };
}

/**
 * Maps a polled `ValidationView.report` to the local `CompanyShellValidateResult`
 * shape — the Track 2 replacement for `useValidateCompanyShellProvision`'s /
 * `useCommitCompanyShellProvision`'s `result`.
 *
 * `report` is nullable to cover `ValidationView.report` being `null`/absent
 * while the validation job is still queued/running; callers are expected to
 * only treat the result as final once the job status is terminal, mirroring
 * Track 1's `result` staying `null` until the workflow poll reaches
 * `COMPLETED`.
 *
 * `rowCount` is the caller's last-known canonical row count (the same array
 * rendered in `CompanyRowsTable`) — Track 2's `ValidationReport` carries no
 * row-count field of its own, unlike Track 1's `CompanyShellValidateResult
 * .row_count`, which was always just `rows.length` at invocation time.
 */
export function mapValidationReportToCompanyShellValidateResult(
  report: ValidationReport | null | undefined,
  rowCount: number
): CompanyShellValidateResult {
  const errors = (report?.errors ?? []).map((error) =>
    toCompanyShellValidationError(error, RowSeverity.error)
  );
  const warnings = (report?.warnings ?? []).map((warning) =>
    toCompanyShellValidationError(warning, RowSeverity.warning)
  );
  return {
    errors: [...errors, ...warnings],
    row_count: rowCount,
    // No Track 2 carrier — see module doc's normalisations residual.
    normalisations: undefined,
  };
}
